/**
 * Static registry for the Workspace Selector: the studios a workspace card can
 * deep-link into, and the sort modes the list offers.
 *
 * Per-workspace display metadata (industry, role, studio access, last visited)
 * is NOT here — the backend doesn't return it yet, so it comes from
 * `__mocks__/workspaceSelector.mock.js` until it does.
 */

/**
 * Studios shown as "OPEN A STUDIO" chips, with their in-app deep-link routes.
 *
 * Every path is a studio ROOT or a static section (Data Studio → Models), never
 * a dynamic entity path: the chip lands after a scoped-token handoff into a
 * DIFFERENT workspace, where an id from the previous one would 404.
 *
 * Routes verified against `apps/core-shell/src/App.jsx` and
 * `shell/navigationConfig.js`. Order here is the order the chips render in.
 */
export const STUDIOS = [
  { id: 'governance-studio', labelKey: 'workspaceSelector.studios.governanceStudio', path: '/governance' },
  { id: 'data-studio', labelKey: 'workspaceSelector.studios.dataStudio', path: '/data-studio/models' },
  { id: 'agentic-studio', labelKey: 'workspaceSelector.studios.agenticStudio', path: '/agentic-studio' },
  // Work Surfaces = the Admin Console, per CT-4941's destination map. NOTE: a
  // real `/work-queues` route now exists in the shell (WorkQueuesEmbed); if
  // Work Surfaces is meant to be that app rather than Admin Studio, this is
  // the single line to change.
  { id: 'work-surfaces', labelKey: 'workspaceSelector.studios.workSurfaces', path: '/admin-studio' },
];

const STUDIO_BY_ID = Object.fromEntries(STUDIOS.map((s) => [s.id, s]));

/** Sort modes cycled by the sort control (Last visited → A–Z → Role). */
export const SORT_MODES = ['recent', 'alpha', 'role'];

/** Resolve a studio's deep-link route by id (safe fallback to the dashboard). */
export function studioRoute(studioId) {
  return STUDIO_BY_ID[studioId]?.path ?? '/dashboard';
}

/** Resolve a studio's i18n label key by id. */
export function studioLabelKey(studioId) {
  return STUDIO_BY_ID[studioId]?.labelKey ?? '';
}
